import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import moment from "moment";
import { useDispatch } from "react-redux";
import { exportSuite } from "../../../features/slices/Suite";
import { useNotification } from "../../../context/NotificationContext";

const statusOptions = [
  "ALL",
  "INIT",
  "FAILED",
  "ABORTED",
  "IN_PROGRESS",
  "SUCCESS",
  "SUCCESS_NOTIFY",
  "ERROR",
];

const SectionLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
    <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
    <Typography
      sx={{
        fontSize: "0.72rem",
        fontWeight: 800,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const ExportDialog = ({ open, onClose }) => {
  const [fromDate, setFromDate] = useState(moment().subtract(1, "months"));
  const [toDate, setToDate] = useState(moment());
  const [selectedStatuses, setSelectedStatuses] = useState(["ALL"]);
  const [reportFormat, setReportFormat] = useState("JSON");
  const [suiteName, setSuiteName] = useState("");
  const [suiteId, setSuiteId] = useState("");

  const dispatch = useDispatch();
  const { notify } = useNotification();

  const disableFilters = Boolean(suiteId) || Boolean(suiteName);

  const getDynamicFileName = () => {
    const timestamp = moment().format("YYYY_MM_DD_HH");
    let filename = "export";
    if (suiteId) {
      filename = `suite_${suiteId}_export`;
    } else if (suiteName) {
      filename = `${suiteName.trim().replace(/\s+/g, "_")}_export`;
    } else if (fromDate && toDate) {
      filename = `export_${fromDate.format("YYYYMMDD")}_to_${toDate.format(
        "YYYYMMDD"
      )}`;
    } else if (
      selectedStatuses &&
      selectedStatuses.length > 0 &&
      !selectedStatuses.includes("ALL")
    ) {
      filename = `export_status_${selectedStatuses.join("_")}`;
    }
    return `${filename}_${timestamp}`;
  };

  const handleExport = () => {
    let exportData = {};
    if (suiteId) {
      exportData = { suite_id: parseInt(suiteId, 10), format: reportFormat };
    } else if (suiteName) {
      exportData = { name: suiteName, format: reportFormat };
    } else {
      exportData = {
        from_date: fromDate.format("YYYY-MM-DD HH:mm:ss"),
        to_date: toDate.format("YYYY-MM-DD HH:mm:ss"),
        status:
          selectedStatuses.length === 0 || selectedStatuses.includes("ALL")
            ? []
            : selectedStatuses,
        format: reportFormat,
      };
    }

    const filteredExportData = Object.fromEntries(
      Object.entries(exportData).filter(
        ([, value]) =>
          value !== null &&
          value !== "" &&
          (!Array.isArray(value) || value.length > 0)
      )
    );
    const query = new URLSearchParams(filteredExportData).toString();

    dispatch(exportSuite(query))
      .unwrap()
      .then((data) => {
        if (!data || data.length === 0) {
          notify("No data found for the given criteria", "info");
          return;
        }
        notify("Export successful", "success");

        let blob;
        let filename;
        const dynamicName = getDynamicFileName();
        if (reportFormat === "CSV") {
          blob = new Blob([data], { type: "text/csv" });
          filename = `${dynamicName}.csv`;
        } else if (reportFormat === "RAW") {
          blob = new Blob([JSON.stringify(data, null, 2)], {
            type: "application/json",
          });
          filename = `${dynamicName}.raw.json`;
        } else {
          blob = new Blob([JSON.stringify(data, null, 2)], {
            type: "application/json",
          });
          filename = `${dynamicName}.json`;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .catch((error) => {
        notify(`Export failed: ${error}`, "error");
      });
  };

  const formats = [
    { value: "JSON", label: "JSON", hint: "Structured" },
    { value: "CSV", label: "CSV", hint: "Spreadsheet" },
    { value: "RAW", label: "RAW", hint: "Unprocessed" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FileDownloadOutlinedIcon color="primary" />
          <span>Export suites</span>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontWeight: 400 }}
        >
          Download a report of suites matching your criteria.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2.5 }}>
          <SectionLabel icon={<DateRangeOutlinedIcon fontSize="small" />}>
            Time range
          </SectionLabel>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <DatePicker
                  label="From date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue || moment())}
                  maxDate={moment().add(6, "months")}
                  slotProps={{
                    textField: { fullWidth: true, disabled: disableFilters },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <DatePicker
                  label="To date"
                  value={toDate}
                  onChange={(newValue) =>
                    setToDate(newValue || moment().add(1, "days"))
                  }
                  minDate={fromDate}
                  maxDate={moment().add(6, "months")}
                  slotProps={{
                    textField: { fullWidth: true, disabled: disableFilters },
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <SectionLabel icon={<FilterAltOutlinedIcon fontSize="small" />}>
            Filter by
          </SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth disabled={disableFilters}>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={(e) => setSelectedStatuses(e.target.value)}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox
                        checked={selectedStatuses.indexOf(status) > -1}
                      />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  or target one suite
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Suite name"
                value={suiteName}
                onChange={(e) => setSuiteName(e.target.value)}
                disabled={Boolean(suiteId)}
                helperText={
                  suiteId ? "Ignored — Suite ID set" : "Overrides filters above"
                }
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Suite ID"
                value={suiteId}
                onChange={(e) => setSuiteId(e.target.value)}
                type="number"
                helperText="Takes priority over everything"
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <SectionLabel icon={<DescriptionOutlinedIcon fontSize="small" />}>
            Format
          </SectionLabel>
          <ToggleButtonGroup
            value={reportFormat}
            exclusive
            onChange={(_, v) => v && setReportFormat(v)}
            fullWidth
            color="primary"
          >
            {formats.map((f) => (
              <ToggleButton
                key={f.value}
                value={f.value}
                sx={{ flexDirection: "column", py: 1, textTransform: "none" }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: "0.85rem" }}>
                  {f.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {f.hint}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          startIcon={<FileDownloadOutlinedIcon />}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
